import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, Bug, CheckCircle, Activity,
  ArrowUpRight, ArrowDownRight, Clock, Zap, Info, RefreshCw
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const severityColors = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e', informational: '#3b82f6' };

function MetricCard({ icon: Icon, label, value, change, trend, color, testId }) {
  return (
    <Card className="metric-card" data-testid={testId}><CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
          <p className="text-3xl font-bold font-heading tracking-tight">{value}</p>
          {change && <div className={`flex items-center gap-1 mt-1 text-xs ${trend === 'up' ? 'text-red-500' : 'text-green-500'}`}>{trend === 'up' ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{change}</div>}
        </div>
        <div className={`p-2.5 rounded-xl ${color}`}><Icon className="h-5 w-5" /></div>
      </div>
    </CardContent></Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (<div className="bg-card border border-border rounded-lg p-3 shadow-xl text-sm"><p className="font-medium mb-1">{label}</p>{payload.map((entry, i) => (<p key={i} style={{ color: entry.color }} className="text-xs">{entry.name}: {entry.value}</p>))}</div>);
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchProducts();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchStats();
    }
  }, [selectedProject, selectedProduct]);

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedProject !== 'all') params.append('project_id', selectedProject);
      if (selectedProduct !== 'all') params.append('product_id', selectedProduct);
      
      const res = await axios.get(`${API}/dashboard/stats?${params.toString()}`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(res.data);
    } catch {}
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
    toast.success('Dashboard refreshed');
  };

  const handleProjectChange = (value) => {
    setSelectedProject(value);
    if (value !== 'all') {
      setSelectedProduct('all');
    }
  };

  const handleProductChange = (value) => {
    setSelectedProduct(value);
    if (value !== 'all') {
      setSelectedProject('all');
    }
  };

  if (loading) {
    return (<div className="flex items-center justify-center h-[60vh]"><div className="text-center"><Shield className="h-10 w-10 text-primary mx-auto mb-3 animate-pulse" /><p className="text-sm text-muted-foreground">Loading security overview...</p></div></div>);
  }

  const trendData = [
    { month: 'Aug', critical: 12, high: 24, medium: 45, low: 18 },
    { month: 'Sep', critical: 8, high: 20, medium: 38, low: 22 },
    { month: 'Oct', critical: 15, high: 28, medium: 42, low: 15 },
    { month: 'Nov', critical: 10, high: 18, medium: 35, low: 20 },
    { month: 'Dec', critical: 6, high: 15, medium: 30, low: 25 },
    { month: 'Jan', critical: stats?.critical || 4, high: stats?.high || 12, medium: stats?.medium || 28, low: stats?.low || 16 },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight" data-testid="dashboard-title">Security Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Welcome back, {user?.name}. Here's your security posture.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedProduct} onValueChange={handleProductChange}>
            <SelectTrigger className="w-[160px] h-9" data-testid="product-filter">
              <SelectValue placeholder="All Products" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Products</SelectItem>
              {products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={selectedProject} onValueChange={handleProjectChange}>
            <SelectTrigger className="w-[160px] h-9" data-testid="project-filter">
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {stats?.projects?.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-dashboard">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 stagger-children">
        <MetricCard icon={Bug} label="Total Vulnerabilities" value={stats?.total_vulnerabilities || 0} change="+12% from last month" trend="up" color="bg-red-500/10 text-red-500" testId="metric-total-vulns" />
        <MetricCard icon={AlertTriangle} label="Critical Issues" value={stats?.critical || 0} change="-8% from last month" trend="down" color="bg-orange-500/10 text-orange-500" testId="metric-critical" />
        <MetricCard icon={Activity} label="Open Findings" value={stats?.open_vulnerabilities || 0} color="bg-blue-500/10 text-blue-500" testId="metric-open" />
        <MetricCard icon={CheckCircle} label="Resolved" value={stats?.resolved || 0} change="+24 this week" trend="down" color="bg-green-500/10 text-green-500" testId="metric-resolved" />
        <MetricCard icon={Info} label="Informational" value={stats?.informational || 0} color="bg-sky-500/10 text-sky-500" testId="metric-informational" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-8 min-w-0 overflow-hidden" data-testid="severity-trend-chart">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Vulnerability Trends</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="99%" height={280} debounce={80}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="critGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/><stop offset="95%" stopColor="#ef4444" stopOpacity={0}/></linearGradient>
                  <linearGradient id="highGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/><stop offset="95%" stopColor="#f97316" stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="month" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="critical" stroke="#ef4444" fill="url(#critGrad)" name="Critical" />
                <Area type="monotone" dataKey="high" stroke="#f97316" fill="url(#highGrad)" name="High" />
                <Area type="monotone" dataKey="medium" stroke="#eab308" fill="transparent" name="Medium" />
                <Area type="monotone" dataKey="low" stroke="#22c55e" fill="transparent" name="Low" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="lg:col-span-4 min-w-0 overflow-hidden" data-testid="severity-distribution">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Severity Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="99%" height={200} debounce={80}>
              <PieChart><Pie data={stats?.severity_distribution || []} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={3} dataKey="value" nameKey="name">
                {(stats?.severity_distribution || []).map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie><Tooltip content={<CustomTooltip />} /></PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap justify-center gap-3 mt-2">
              {(stats?.severity_distribution || []).map(item => (
                <div key={item.name} className="flex items-center gap-1.5 text-xs"><span className="h-2.5 w-2.5 rounded-full" style={{ background: item.color }} /><span className="text-muted-foreground">{item.name}</span><span className="font-medium">{item.value}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-4" data-testid="risk-score-card">
          <CardHeader className="pb-2"><CardTitle className="text-base font-heading flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Risk Score</CardTitle></CardHeader>
          <CardContent className="flex flex-col items-center py-6">
            <div className="relative w-32 h-32 mb-4">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" className="stroke-muted" />
                <circle cx="50" cy="50" r="42" fill="none" strokeWidth="8" stroke={stats?.risk_score > 70 ? '#ef4444' : stats?.risk_score > 40 ? '#eab308' : '#22c55e'} strokeDasharray={`${(stats?.risk_score || 0) * 2.64} 264`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center"><span className="text-3xl font-bold font-heading">{stats?.risk_score || 0}</span></div>
            </div>
            <p className="text-sm text-muted-foreground text-center">{stats?.risk_score > 70 ? 'High Risk - Immediate action required' : stats?.risk_score > 40 ? 'Moderate Risk - Review recommended' : 'Low Risk - Good security posture'}</p>
          </CardContent>
        </Card>

        <Card className="lg:col-span-8" data-testid="recent-vulns-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-heading">Recent Findings</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-primary" onClick={() => navigate('/vulnerabilities')} data-testid="view-all-vulns">View All <ArrowUpRight className="h-3 w-3 ml-1" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[260px]">
              <div className="space-y-2">
                {(stats?.recent_vulnerabilities || []).map(vuln => (
                  <div key={vuln.vuln_id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate('/vulnerabilities')} data-testid={`recent-vuln-${vuln.vuln_id}`}>
                    <div className="h-2 w-2 rounded-full shrink-0" style={{ background: severityColors[vuln.severity] }} />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{vuln.title}</p><p className="text-xs text-muted-foreground truncate">{vuln.asset}</p></div>
                    <Badge variant="outline" className={`shrink-0 text-xs ${vuln.severity === 'critical' ? 'border-red-500/30 text-red-500' : vuln.severity === 'high' ? 'border-orange-500/30 text-orange-500' : vuln.severity === 'medium' ? 'border-yellow-500/30 text-yellow-500' : vuln.severity === 'informational' ? 'border-blue-500/30 text-blue-500' : 'border-green-500/30 text-green-500'}`}>{vuln.severity}</Badge>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0"><Clock className="h-3 w-3" />{new Date(vuln.created_at).toLocaleDateString()}</div>
                  </div>
                ))}
                {(!stats?.recent_vulnerabilities || stats.recent_vulnerabilities.length === 0) && <div className="text-center py-8 text-muted-foreground text-sm">No vulnerabilities found. Run a scan to get started.</div>}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
